import { formatDate as angularFormatDate } from '@angular/common';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Application, DefaultMimeTypes } from '@enums/application.enums';
import { REGEX } from '@core/validation';
import { AsYouType } from 'libphonenumber-js';
import { AbstractControl } from '@angular/forms';
import { environment } from '@environments/environment';
import { Fields, StoreShareConstants, WEEK_LABELS } from '@constants/constant';
import moment from 'moment';
import { AVAILABLE_THEMES, THEME_COLORS, ThemeName } from '@constants/theme-colors.constant';
import isArray from 'lodash/isArray';
import { STORE_MONETIZE_TYPE } from '@shared/components/share-details-preview/constant';
import { BIO_PRODUCT_PREFIXES, BIO_URL_SEGMENTS } from '@modules/edit-preference/link-in-bio/create-creator-bio/linkin-bio.contants';
import {
  PORTFOLIO_CTA_BUTTON_TO_TYPE,
  PORTFOLIO_CTA_COLLECTION,
  PORTFOLIO_CTA_INDIVIDUAL,
} from '@shared/components/portfolio-share-modal/constant';
import { PORTFOLIO_TYPE_CONSTS } from '@modules/edit-preference/components/portfolio/components/add-edit-portfolio-type/add-edit-portfolio-type.fields';
import { STEP_1_BASIC_INFO_CONSTS } from '@modules/edit-preference/components/portfolio/components/add-edit-album-portfolio/step-1-basic-info/step-1-basic-info.fields';
export function getVideoMode(width: number, height: number) {
  return width > height ? Application.LANDSCAPE : Application.PORTRAIT;
}

/** Parses API `event_date` (`YYYY-MM-DD`) as a local calendar date (no UTC shift). */
export function parseEventCalendarDate(eventDate: unknown): Date | null {
  if (eventDate == null || eventDate === '') {
    return null;
  }

  const raw = String(eventDate).trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Date(`${raw}T00:00:00`);
  }

  const parsed = new Date(String(eventDate));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatEventCalendarDatePart(
  eventDate: unknown,
  format: 'MMM' | 'd' | 'yyyy'
): string {
  const date = parseEventCalendarDate(eventDate);
  if (!date) {
    return '';
  }

  if (format === 'd') {
    return String(date.getDate());
  }

  return angularFormatDate(date, format, 'en-US');
}

/**
 * Event calendar / ticket UI: formats API `event_date` + `event_time` as
 * `"MMM d, y at h:mm AM/PM"` with no timezone (e.g. `Mar 26, 2026 at 11:50 AM`).
 * Pass `CommonService.prototype.formatTo12HourTime` (or equivalent) for 12h conversion.
 */
export function formatEventDateTimeLine(
  eventDate: unknown,
  eventTime: unknown,
  formatTo12HourTime: (time24: string) => string
): string {
  if (!eventDate && !eventTime) {
    return '';
  }
  const date = parseEventCalendarDate(eventDate);
  const dateStr = date
    ? angularFormatDate(date, 'MMM d, y', 'en-US')
    : '';
  const timeStr = eventTime
    ? formatTo12HourTime(String(eventTime).trim())
    : '';
  return [dateStr, timeStr ? `at ${timeStr}` : ''].filter(Boolean).join(' ');
}

/**
 * Formats a redeemed-at (or similar) epoch ms timestamp in local time as
 * `"MMM d, y at h:mm AM/PM"` with no timezone label.
 */
export function formatEventTimestampLocalDateTime(
  timestamp: number,
  formatTo12HourTime: (time24: string) => string
): string {
  const d = new Date(timestamp);
  const datePart = angularFormatDate(d, 'MMM d, y', 'en-US');
  const pad = (n: number) => n.toString().padStart(2, '0');
  const time24 = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const timeStr = formatTo12HourTime(time24);
  return `${datePart} at ${timeStr}`;
}

export const loadVideoMetadata = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      const videoMode = getVideoMode(video.videoWidth, video.videoHeight);
      resolve(videoMode);
    };
    video.onerror = () => {
      reject(new Error('err'));
    };
  });
};


export const getBackgroundColor = (color, opacity = '0.2') => {
  if (!color) {
    return '';
  }
  // Convert hex to RGB
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  // Return RGBA with {opacity}% opacity
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const loadVideoMetadataV2 = (file, url = null) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url ? url : URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      const duration = video.duration;
      const width = video.videoWidth;
      const height = video.videoHeight;
      const videoMode = getVideoMode(video.videoWidth, video.videoHeight);
      resolve({ videoMode, duration, width, height });
    };
    video.onerror = (err) => {
      reject(new Error(err?.toString()));
    };
  });
};

export const getCurrentTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const toTitleCase = (stringName: string) => {
  if (!stringName) return stringName;
  stringName = stringName.toLowerCase();
  return stringName
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const transformSpecialCharacters = (
  stringName: string,
  isEncoding: boolean
) => {
  const replacements = isEncoding
    ? {
      ' ': '~',
      '(': ':',
      ')': '*'
    }
    : {
      '~': ' ',
      ':': '(',
      '*': ')'
    };
  const regex = isEncoding ? /[ \(\)]/g : /[~:*]/g;
  return stringName?.replace(regex, (char) => replacements[char]) || '';
};

// Format the date in Mountain Time (MT)
export const convertToMountainTime = (timestamp) => {
  const date = new Date(timestamp);

  const options: any = {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);

  // Extract the formatted parts and construct the desired format
  const month = parts.find((part) => part.type === 'month').value;
  let day = parts.find((part) => part.type === 'day').value;
  const year = parts.find((part) => part.type === 'year').value;
  const hour = parts.find((part) => part.type === 'hour').value;
  const minute = parts.find((part) => part.type === 'minute').value;
  const dayPeriod = parts.find((part) => part.type === 'dayPeriod').value;

  day = day.replace(REGEX.START_WITH_ZERO, '');
  // Construct the formatted date string
  const formattedDate = `${month} ${day}, ${year} ${hour}:${minute} ${dayPeriod} (MT)`;
  return formattedDate;
};

export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export const formatDateV2 = (date: Date): string => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];
  const year = date.getFullYear();
  const month = monthNames[date.getMonth()];
  const day = date.getDate().toString();
  return `${month} ${day}, ${year}`;
};

export type CustomerAddonSlotMap = Record<
  string,
  { from?: string; to?: string }
>;

export const formatCustomerAddonSlotDisplay = (
  slot: CustomerAddonSlotMap | null | undefined
): string => {
  if (!slot || typeof slot !== 'object') {
    return '';
  }
  const dateKeys = Object.keys(slot).sort();
  if (!dateKeys.length) {
    return '';
  }
  const currentUserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateKey = dateKeys[0];
  const range = slot[dateKey];
  if (!range?.from || !range?.to) {
    return '';
  }
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return `${formatTo12HourTime(range.from)} – ${formatTo12HourTime(range.to)}`;
  }
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  return `${datePart}, ${formatTo12HourTime(range.from)} – ${formatTo12HourTime(range.to)} ${currentUserTimezone}`;
};

export const getIndex = (
  totalIndex: number,
  boxIndex: number,
  category: Array<Object>
): number => {
  const index = category
    .slice(0, totalIndex)
    .reduce((sum, item) => sum + item['options'].length, 0);
  return index + boxIndex + 1;
};

export const formatPhoneNumber = (
  value: string,
  countryISO?,
  countryCode?: string
) => {
  if (!value) return value;
  const asYouType = new AsYouType(countryISO?.toUpperCase());
  const openParenthesesIndex = value?.lastIndexOf('(');
  const closeParenthesesIndex = value?.lastIndexOf(')');
  if (openParenthesesIndex > closeParenthesesIndex) {
    value =
      value?.slice(0, openParenthesesIndex) +
      value?.slice(openParenthesesIndex + 1, value?.length - 1);
  }
  value = countryCode === '+1' ? value?.slice(3) : value;
  value = asYouType.input(value);
  return countryCode === '+1' ? value : value?.slice(countryCode.length + 1);
};

export const convertTo24Hour = (time12h) => {
  let [time, modifier] = time12h.split(' ');
  if (!modifier) {
    return time12h;
  }
  let [hours, minutes] = time.split(':');
  hours = (hours % 12) + (modifier === 'PM' ? 12 : 0);
  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

export const formatTo12HourTime = (time24) => {
  if (!time24) {
    return '';
  }
  if (time24.split(' ')?.length === 2) {
    return time24;
  }
  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutesFormatted = minutes < 10 ? '0' + minutes : minutes;
  return `${hours}:${minutesFormatted} ${ampm}`;
}
export const getCurrentDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

export const convertDateToEpochAtMidnight = (dateValue: any): number | null => {
  if (!dateValue) {
    return null;
  }
  let date: Date;
  if (dateValue instanceof Date) {
    date = new Date(dateValue);
  } else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  } else if (typeof dateValue === 'number') {
    date = new Date(dateValue);
  } else {
    return null;
  }
  if (isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);  
  return date.getTime();
};
export const convertTimestampToDateAtLocalMidnight = (timestamp: any): Date | null => {
  if (!timestamp) {
    return null;
  }
  
  let dateValue: number | null = null;
  
  if (typeof timestamp === 'number') {
    dateValue = timestamp;
  } else if (typeof timestamp === 'string') {
    const parsedNumber = parseInt(timestamp, 10);
    if (!isNaN(parsedNumber) && parsedNumber.toString() === timestamp) {
      dateValue = parsedNumber;
    } else {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        dateValue = date.getTime();
      }
    }
  } else if (timestamp instanceof Date) {
    dateValue = timestamp.getTime();
  }
  
  if (dateValue === null || isNaN(dateValue)) {
    return null;
  }  
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(year, month, day, 0, 0, 0, 0);
};
export const sortOnSequenceNumber = (data) => {
  data.sort(
    (hastag1, hastag2) =>
      (hastag1?.sequence_number ? hastag1.sequence_number : Infinity) -
      (hastag2?.sequence_number ? hastag2.sequence_number : Infinity)
  );
};
export const validDOB = () => {
  const dt = new Date();
  const year = dt.getFullYear() - Application.THIRTEEN;
  const today = `${year}-${dt.getMonth() + 1}-${dt.getDate()}`;
  const date = new Date(today);
  return date;
};
export const keyPress = (
  event: KeyboardEvent,
  maxLength: number,
  control: AbstractControl
) => {
  const currentLength = control?.value?.number.length || 0;
  const isDigit = REGEX.ONLY_DIGITS.test(event.key);
  if (currentLength >= maxLength && isDigit) {
    event.preventDefault();
  }
};

export const topFanE = (text, newKey) => {
  const combined = text + '|' + newKey;

  function shiftChar(char) {
    if (/[a-zA-Z]/.test(char)) {
      const code = char.charCodeAt(0);
      const isUpperCase = char === char.toUpperCase();
      const shift = isUpperCase ? 65 : 97;
      return String.fromCharCode(((code - shift + 9) % 26) + shift);
    }
    return char;
  }

  return combined.split('').map(shiftChar).join('');
};
export const topFanD = (text) => {
  function unshiftChar(char) {
    if (/[a-zA-Z]/.test(char)) {
      let code = char.charCodeAt(0);
      let isUpperCase = char === char.toUpperCase();
      let shift = isUpperCase ? 65 : 97;
      return String.fromCharCode(((code - shift - 9 + 26) % 26) + shift);
    }
    return char;
  }

  const newText = text.split('').map(unshiftChar).join('');

  return newText.split('|');
};
export function extractTimestampFromVideoUrl(url: string): number | null {
  const match = url.match(/post_(\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}
export function extractTimestampFromCourse(url: string): number | null {
  const match = url.match(/course_(\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}
export function extractTimestampFromPortfolio(url: string): number | null {
  const match = url.match(/portfolio_(\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}
export function extractTimestampFromChatroom(url: string): number | null {
  const match = url.match(/chatroom_(\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}
export function generateRandomId(length = 6) {
  return Math.random().toString(36).substring(2, 2 + length);
}
export function isHLS(path: string): boolean {
  return path?.endsWith('.m3u8');
}
export function getVideoOptions(filePath: string) {
  return {
    sources: [
      {
        src: filePath, // Dynamically use the updated file path
        type: 'application/x-mpegURL'
      }
    ],
    autoplay: false,
    loop: false,
    playsinline: true,
    muted: true,
    preload: 'auto',
    controls: false,
    controlBar: {
      playToggle: false,
      muteToggle: false,
      fullscreenToggle: false,
      volumePanel: false,
      remainingTimeDisplay: false,
      currentTimeDisplay: false,
      timeDivider: false,
      progressControl: false,
      seekBar: false,
      durationDisplay: false
    },
    loadingSpinner: false,
    bigPlayButton: false
  };
}

export function extractVideoModeFromFileName(path: string, prefixes: string[]): string | null {
  if (!prefixes.length) return null;
  const filename = path.split('/').pop() || '';

  const escaped = prefixes
    .map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  // Regex to match the pattern: post_<timestamp>_<randomId>_<videoMode>_
  const pattern = new RegExp(`^(${escaped})_[^_]+_[^_]+_([^_]+)_`);
  const match = filename.match(pattern);
  return match?.[2] ?? null;
}
export const sortResourcesData = (data) => {
  return data.sort((a, b) => {
    const hasSeqA = a.sequence_number !== undefined && a.sequence_number !== null;
    const hasSeqB = b.sequence_number !== undefined && b.sequence_number !== null;
    if (hasSeqA && hasSeqB) {
      return a.sequence_number - b.sequence_number;
    } else if (hasSeqA) {
      return -1;
    } else if (hasSeqB) {
      return 1;
    } else {
      const timestampA = a.publish_timestamp ?? a.created_at ?? 0;
      const timestampB = b.publish_timestamp ?? b.created_at ?? 0;
      return timestampB - timestampA;
    }
  });
};

export function getTrialEndDate(days: number) {
  const now = new Date(); // current time
  if (days) {
    now.setDate(now.getDate() + Number(days)); // add days
  }
  return now.getTime();
}

export const base64ToImage = (base64String: string, filename: string) => {
  const blob = base64ToBlob(base64String, DefaultMimeTypes.IMAGE);
  const file = new File([blob], filename, { type: DefaultMimeTypes.IMAGE });
  return file;
}

export const base64ToBlob = (base64String: string, mimeType: string) => {
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType });
}

export function getDomain(url: string): string {
  try {
    // Fallback URL if input is invalid
    const safeUrl = url || environment.websiteURL || 'https://topfans.com';

    // Check if URL constructor is available (browser environment)
    if (typeof URL !== 'undefined') {
      const newUrl = new URL(safeUrl);
      const parts = newUrl.hostname.split('.');
      const domain = parts.length > 2
        ? parts.slice(parts.length - 2).join('.')
        : newUrl.hostname;
      return domain;
    } else {
      // Fallback for environments where URL constructor is not available
      // Extract domain using regex
      const match = safeUrl.match(/^https?:\/\/([^\/]+)/);
      if (match) {
        const hostname = match[1];
        const parts = hostname.split('.');
        return parts.length > 2
          ? parts.slice(parts.length - 2).join('.')
          : hostname;
      }
      return 'topfans.com';
    }
  } catch (error) {
    return 'topfans.com';
  }
}
export function ensureEncoded(value: string): string {
  if (!value) return value;

  try {
    // Try decoding it
    const decoded = decodeURIComponent(value);

    // If re-encoding gives back the same string, it was already encoded
    if (encodeURIComponent(decoded) === value) {
      return value; // already encoded
    }

    // Otherwise, encode it
    return encodeURIComponent(value);
  } catch {
    // If it can't be decoded at all, assume it's not encoded
    return encodeURIComponent(value);
  }
}

export function extractTextFromHTML(htmlString: string) {
  if (!htmlString) return "";

  // Remove script and style contents first
  const noScriptStyle = htmlString
    .replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, "")
    .replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, "");

  // Remove all remaining HTML tags
  const textOnly = noScriptStyle.replace(/<[^>]+>/g, " ");

  // Normalize whitespace
  return textOnly.replace(/\s+/g, " ").trim();
}

export function getDateRangeByFilter(filter: string) {
  const today = new Date();

  let startDate: Date | null = null;
  let endDate: Date | null = null;

  switch (filter) {
    case WEEK_LABELS.THIS_WEEK:
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;

    case WEEK_LABELS.LAST_WEEK:
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay() - 7);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;

    case WEEK_LABELS.THIS_MONTH:
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;

    case WEEK_LABELS.LAST_MONTH:
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    case WEEK_LABELS.TODAY:
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;

    case WEEK_LABELS.ALL_TIME:
      startDate = new Date(2025, 0, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;

    default:
      // default = this-week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
  }
  return { startDate, endDate };
}

export function setCSSVariables(colors, selectedTheme: ThemeName = AVAILABLE_THEMES.dark) {
  // Skip if not in browser environment (SSR/prerendering), body not yet available, or style API missing
  const body = typeof document !== 'undefined' ? document.body : null;
  if (!body?.style?.setProperty) return;
  if (!!environment.whiteLabelConfig) return;

  // Set white-label/custom colors (user-configurable at runtime)
  body.style.setProperty('--configBgColor', colors.bg_color);
  body.style.setProperty('--configThemeColor', Application.DEFAULT_THEME_COLOR);
  body.style.setProperty('--configSecondaryBgColor', colors.secondary_bg_color);
  body.style.setProperty('--configPostTextColor', colors.post_text_color);
  body.style.setProperty('--configButtonTextColor', colors.button_text_color);
  body.style.setProperty('--configBgTextColor', colors.bg_text_color);

  const theme = THEME_COLORS[selectedTheme];

  // Backgrounds
  body.style.setProperty('--theme-bg-primary', theme.backgrounds.primary);
  body.style.setProperty('--theme-bg-secondary', theme.backgrounds.secondary);
  body.style.setProperty('--theme-bg-tertiary', theme.backgrounds.tertiary);
  body.style.setProperty('--theme-bg-modal', theme.backgrounds.modal);
  body.style.setProperty('--theme-bg-input', theme.backgrounds.input);
  body.style.setProperty('--theme-bg-card', theme.backgrounds.card);
  body.style.setProperty('--theme-bg-dark', theme.backgrounds.dark);
  body.style.setProperty('--theme-bg-light', theme.backgrounds.light);
  body.style.setProperty('--theme-bg-icon-light', theme.backgrounds.iconLight);
  body.style.setProperty('--theme-bg-overlay-dark', theme.backgrounds.overlayDark);
  body.style.setProperty('--theme-bg-overlay-dark-alt', theme.backgrounds.overlayDarkAlt);
  body.style.setProperty('--theme-bg-footer', theme.backgrounds.footer);

  // Text & Typography
  body.style.setProperty('--theme-text-primary', theme.text.primary);
  body.style.setProperty('--theme-text-secondary', theme.text.secondary);
  body.style.setProperty('--theme-text-placeholder', theme.text.placeholder);
  body.style.setProperty('--theme-text-dark', theme.text.dark);
  body.style.setProperty('--theme-text-disabled', theme.text.disabled);
  body.style.setProperty('--theme-text-inverse', theme.text.inverse);
  body.style.setProperty('--theme-text-danger', theme.text.danger);

  // Interactive States
  body.style.setProperty('--theme-interactive-link', theme.interactive.link);
  body.style.setProperty('--theme-interactive-hover', theme.interactive.hover);
  body.style.setProperty('--theme-interactive-focus', theme.interactive.focus);
  body.style.setProperty('--theme-interactive-button', theme.interactive.button);
  body.style.setProperty('--theme-interactive-accent', theme.interactive.accent);

  // Borders & Dividers
  body.style.setProperty('--theme-border-primary', theme.borders.primary);
  body.style.setProperty('--theme-border-secondary', theme.borders.secondary);
  body.style.setProperty('--theme-border-transparent', theme.borders.transparent);

  // Gradients
  body.style.setProperty('--theme-gradient-primary', theme.gradients.primary);
  body.style.setProperty('--theme-gradient-secondary', theme.gradients.secondary);
  body.style.setProperty('--theme-gradient-overlay-dark', theme.gradients.overlayDark);
  body.style.setProperty('--theme-gradient-overlay-light', theme.gradients.overlayLight);
  body.style.setProperty('--theme-gradient-sidebar-overlay', theme.gradients.sidebarOverlay);
  body.style.setProperty('--theme-gradient-profile-card', theme.gradients.profileCard);
  body.style.setProperty('--theme-gradient-button-primary', theme.gradients.buttonPrimary);
  body.style.setProperty('--theme-gradient-button-secondary', theme.gradients.buttonSecondary);
  body.style.setProperty('--theme-gradient-icon-text', theme.gradients.iconText);

  // Special & Utility
  body.style.setProperty('--theme-search-bg', theme.special.searchBg);
  body.style.setProperty('--theme-search-bg-mobile', theme.special.searchBgMobile);
  body.style.setProperty('--theme-search-bg-desktop', theme.special.searchBgDesktop);
  body.style.setProperty('--theme-message-strip', theme.special.messageStrip);
  body.style.setProperty('--theme-shadow-light', theme.special.shadowLight);
}
export const formatToLocalTime = (timestamp: string): string => {
  const date = moment(timestamp);

  if (!date.isValid()) {
    throw new Error(Fields.INVALID_TIMESTAMP);
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localDate = date.tz(timeZone);
  const formattedDate = localDate.format('MMM DD, YYYY h:mm A');

  const timeZoneAbbreviation = localDate.format('z');

  return `${formattedDate} (${timeZoneAbbreviation})`;
};

export function hexToRgb(hex: string): string {
  const match = hex.trim().replace('#', '');
  const bigint = parseInt(match, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}

export function extractWhiteLabelConfig() {
  let currentConfig = null;
  if (environment?.whiteLabelConfig) {
    // Check if whiteLabelConfig is already an object or a JSON string
    if (typeof environment.whiteLabelConfig === 'string') {
      try {
        currentConfig = JSON.parse(environment.whiteLabelConfig);
      } catch (error) {
        console.error('Error parsing whiteLabelConfig JSON:', error);
        currentConfig = null;
      }
    } else {
      // It's already an object
      currentConfig = environment.whiteLabelConfig;
    }
  }
  return currentConfig;
}

export function isValidWhiteLabelConfig(config: any): boolean {
  return config && config.data && Object.keys(config.data).length > 0;
}

export function checkValidImage(event: Event, placeholder_icon: string) {
  const img = event.target as HTMLImageElement;
  if (img.naturalWidth === 0) {
    const wrapperDiv = document.createElement('div');
    wrapperDiv.classList.add('image-error-placeholder-wrapper');
    const em = document.createElement('em');
    if (placeholder_icon) {
      em.classList.add(placeholder_icon);
    } else {
      em.classList.add('icon-liteempty-product');
    }
    em.style.fontSize = '30px';
    wrapperDiv.appendChild(em);
    img.parentElement?.replaceChild(wrapperDiv, img);
  }
}

export function getFormatedAddressFromAddressComponents(address_components: any[]): string {
  let loc: any[] = [];
  let formatedAddress = '';
  loc = isArray(address_components) && address_components.filter((addComp) => {
    return (
      !addComp.types.includes('neighborhood') &&
      !addComp.types.includes('administrative_area_level_2') &&
      !addComp.types.includes('postal_code')
    );
  });
  loc = isArray(loc) && loc?.filter(
    (v, i, a) => a.findIndex((v2) => v.long_name === v2.long_name) === i
  );
  isArray(loc) && loc?.forEach((e, i) => {
    formatedAddress += e.long_name + (i + 1 < loc.length ? ', ' : '');
  });
  return formatedAddress;
}

export function getStreetLineFromAddressComponents(address_components: any[]): string {
  if (!isArray(address_components)) {
    return '';
  }

  const long = (type: string): string => {
    const c = address_components.find((x: any) => x.types?.includes(type));
    return (c?.long_name && String(c.long_name).trim()) || '';
  };
  const num = long('street_number');
  const route = long('route');
  const sub = long('subpremise');
  const floor = long('floor');
  const room = long('room');
  const parts = [num, route, floor, room, sub].filter(Boolean);
  if (parts.length) {
    return parts.join(' ').trim();
  }
  const premise = long('premise');
  if (premise) {
    return premise;
  }
  const intersection = long('intersection');
  if (intersection) {
    return intersection;
  }
  return long('establishment') || '';
}

export function getFirstFormattedAddressSegment(formatted: string): string {
  const t = typeof formatted === 'string' ? formatted.trim() : '';
  if (!t) {
    return '';
  }
  const first = t.split(',')[0]?.trim() ?? '';
  return first;
}

const STORE_SHARE_MAX_LINES = 3;
const STORE_SHARE_MAX_LENGTH = 200;

/**
 * Escape HTML entities for safe display
 * @param text - Raw text to escape
 * @returns HTML-escaped text
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Convert HTML to plain text for use in title attributes (tooltips)
 * Converts <br> tags to newlines and strips other HTML tags
 * @param html - HTML string that may contain <br> tags
 * @returns Plain text with newlines instead of <br> tags
 */
export function htmlToPlainTextForTitle(html: string): string {
  if (!html) return '';
  // Convert <br> and <br/> tags to newlines first
  let text = html.replace(/<br\s*\/?>/gi, '\n');
  // Strip remaining HTML tags
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.textContent || div.innerText || '';
}

/**
 * Truncate description by max lines or max length
 * @param description - The description text to truncate
 * @param maxLines - Maximum number of lines (default: 3)
 * @param maxLength - Maximum character length (default: 200)
 * @returns Truncated description with ellipsis if needed
 */
export function truncateDescription(
  description: string,
  maxLines: number = STORE_SHARE_MAX_LINES,
  maxLength: number = STORE_SHARE_MAX_LENGTH
): string {
  if (!description) return '';
  const lines = description.split('\n');
  const exceedsLength = description.length > maxLength;
  const exceedsLines = lines.length > maxLines;

  if (exceedsLines) {
    return `${lines.slice(0, maxLines).join('\n')}...`;
  } else if (exceedsLength) {
    return `${description.substring(0, maxLength)}...`;
  }
  return description;
}

/**
 * Truncate HTML description by max lines or max length, preserving HTML tags like <br>
 * @param htmlDescription - The HTML description to truncate (may contain <br> tags)
 * @param maxLines - Maximum number of lines (default: 3)
 * @param maxLength - Maximum character length (default: 200)
 * @returns Truncated HTML description with ellipsis if needed
 */
export function truncateHtmlDescription(
  htmlDescription: string,
  maxLines: number = STORE_SHARE_MAX_LINES,
  maxLength: number = STORE_SHARE_MAX_LENGTH
): string {
  if (!htmlDescription) return '';
  
  // Convert <br> and <br/> tags to newlines for easier line-based truncation
  const normalized = htmlDescription.replace(/<br\s*\/?>/gi, '\n');
  const lines = normalized.split('\n');
  const exceedsLength = htmlDescription.length > maxLength;
  const exceedsLines = lines.length > maxLines;

  if (exceedsLines) {
    const truncatedLines = lines.slice(0, maxLines);
    const truncated = truncatedLines.join('\n');
    // Convert newlines back to <br> tags
    return truncated.replace(/\n/g, '<br>') + '...';
  } else if (exceedsLength) {
    return `${htmlDescription.substring(0, maxLength)}...`;
  }
  return htmlDescription;
}

/**
 * Interface for store share card data
 */
export interface StoreShareData {
  image: string;
  title: string;
  description: string;
  detailUrl: string;
}

/**
 * Interface for portfolio share card data
 */
export interface PortfolioShareData {
  image: string;
  title: string;
  description: string;
  detailUrl: string;
  portfolioType: string; // MUSIC, PODCAST, VIDEO, CUSTOM
  isIndividual?: boolean; // true = single song/episode/video, false = album/series
}

/**
 * Get CTA button label for portfolio share based on type and individual vs collection.
 * Individual: Listen Music, Listen To Podcast, Watch Video
 * Collection: View Album, View Podcast Series, View Video Series
 */
export function getPortfolioCtaLabel(portfolioType: string, isIndividual: boolean = true): string {
  const typeKey = (portfolioType || '').toUpperCase();
  const labels = isIndividual ? PORTFOLIO_CTA_INDIVIDUAL : PORTFOLIO_CTA_COLLECTION;
  return labels[typeKey] || (isIndividual ? PORTFOLIO_TYPE_CONSTS.VIEW_NOW : PORTFOLIO_TYPE_CONSTS.VIEW_NOW);
}

/**
 * Interface for feed content (post) share card data
 */
export interface FeedContentShareData {
  image: string;
  title: string;
  description: string;
  detailUrl: string;
  postType: string;
  postSk: string; // for disabled items lookup
}

export interface EventTicketShareData {
  image: string;
  title: string;
  detailUrl: string;
  eventDate: string;
  venueName: string;
  eventSk: string;
  eventTime: string;
  timezone: string;
  city: string;
  state: string;
  country: string;
}


/**
 * Populate a store share placeholder div with content.
 * If no node is provided, creates a new div element.
 * @param data - Store share card data
 * @param node - Optional existing div element (e.g., from Quill's super.create())
 * @returns HTMLElement configured as a store share placeholder
 */
export function populateStoreSharePlaceholder(
  data: StoreShareData,
  node?: HTMLElement
): HTMLElement {
  const { image, detailUrl } = data;
  const title = decodeHtmlEntities(data.title ?? '');
  const description = decodeHtmlEntities(data.description ?? '');
  const truncatedDescription = truncateDescription(description);

  const container = node || document.createElement('div');
  container.setAttribute('contenteditable', 'false');
  container.classList.add('store-share-placeholder', 'store-share-card-table');
  container.setAttribute('data-image', image);
  container.setAttribute('data-title', title);
  container.setAttribute('data-description', description);
  container.setAttribute('data-detail-url', detailUrl);

  const imageDiv = document.createElement('div');
  imageDiv.className = 'store-share-image';
  const img = document.createElement('img');
  img.src = image;
  img.alt = title;
  imageDiv.appendChild(img);

  const contentDiv = document.createElement('div');
  contentDiv.className = 'store-share-content';

  const contentInner = document.createElement('div');
  contentInner.className = 'store-share-content-inner';

  const titleP = document.createElement('p');
  titleP.className = 'store-share-title';
  titleP.title = title;
  titleP.textContent = title;

  const buttonA = document.createElement('a');
  buttonA.href = detailUrl;
  buttonA.className = 'store-share-button';
  buttonA.textContent = 'BUY NOW';

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'store-share-button-container';
  buttonContainer.appendChild(buttonA);

  contentInner.appendChild(titleP);
  if (truncatedDescription && truncatedDescription.trim()) {
    const descP = document.createElement('p');
    descP.className = 'store-share-description';
    descP.title = description;
    descP.textContent = truncatedDescription;
    contentInner.appendChild(descP);
  }
  contentInner.appendChild(buttonContainer);
  contentDiv.appendChild(contentInner);

  container.appendChild(imageDiv);
  container.appendChild(contentDiv);

  return container;
}

/**
 * Populate a portfolio share placeholder div with content.
 * @param data - Portfolio share card data
 * @param node - Optional existing div element (e.g., from Quill's super.create())
 * @returns HTMLElement configured as a portfolio share placeholder
 */
export function populatePortfolioSharePlaceholder(
  data: PortfolioShareData,
  node?: HTMLElement
): HTMLElement {
  const { image, detailUrl, portfolioType, isIndividual = true } = data;
  const title = decodeHtmlEntities(data.title ?? '');
  const description = decodeHtmlEntities(data.description ?? '');
  const truncatedDescription = truncateDescription(description);
  const buttonText = getPortfolioCtaLabel(portfolioType, isIndividual);

  const container = node || document.createElement('div');
  container.setAttribute('contenteditable', 'false');
  container.classList.add('portfolio-share-placeholder', 'portfolio-share-card-table');
  container.setAttribute('data-image', image);
  container.setAttribute('data-title', title);
  container.setAttribute('data-description', description);
  container.setAttribute('data-detail-url', detailUrl);
  container.setAttribute('data-portfolio-type', (portfolioType || '').toUpperCase());
  container.setAttribute('data-is-individual', String(isIndividual !== false));

  const imageDiv = document.createElement('div');
  imageDiv.className = 'portfolio-share-image';
  const img = document.createElement('img');
  img.src = image;
  img.alt = title;
  imageDiv.appendChild(img);

  const contentDiv = document.createElement('div');
  contentDiv.className = 'portfolio-share-content';

  const contentInner = document.createElement('div');
  contentInner.className = 'portfolio-share-content-inner';

  const titleP = document.createElement('p');
  titleP.className = 'portfolio-share-title';
  titleP.title = title;
  titleP.textContent = title;

  const buttonA = document.createElement('a');
  buttonA.href = detailUrl;
  buttonA.className = 'portfolio-share-button';
  buttonA.textContent = buttonText;

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'portfolio-share-button-container';
  buttonContainer.appendChild(buttonA);

  contentInner.appendChild(titleP);
  if (truncatedDescription && truncatedDescription.trim()) {
    const descP = document.createElement('p');
    descP.className = 'portfolio-share-description';
    descP.title = description;
    descP.textContent = truncatedDescription;
    contentInner.appendChild(descP);
  }
  contentInner.appendChild(buttonContainer);
  contentDiv.appendChild(contentInner);

  container.appendChild(imageDiv);
  container.appendChild(contentDiv);

  return container;
}

/**
 * Populate a feed content share placeholder div with content.
 * When no image is provided, the image section is omitted (text + button only).
 */
export function populateFeedContentSharePlaceholder(
  data: FeedContentShareData,
  node?: HTMLElement
): HTMLElement {
  const { image, detailUrl, postType, postSk } = data;
  const title = decodeHtmlEntities(data.title ?? '');
  const description = decodeHtmlEntities(data.description ?? '');
  const truncatedDescription = truncateDescription(description);
  const hasImage = !!(image && image.trim());
  const isArticle = (postType || '').toLowerCase() === 'article';
  const showDescription = !isArticle;

  const container = node || document.createElement('div');
  container.setAttribute('contenteditable', 'false');
  container.classList.add('feed-content-share-placeholder', 'feed-content-share-card-table');
  container.setAttribute('data-image', image || '');
  container.setAttribute('data-title', title);
  container.setAttribute('data-description', description);
  container.setAttribute('data-detail-url', detailUrl);
  container.setAttribute('data-post-type', (postType || '').toLowerCase());
  container.setAttribute('data-post-sk', postSk || '');

  if (hasImage) {
    const imageDiv = document.createElement('div');
    imageDiv.className = 'feed-content-share-image';
    const img = document.createElement('img');
    img.src = image;
    img.alt = title;
    imageDiv.appendChild(img);
    container.appendChild(imageDiv);
  }

  const contentDiv = document.createElement('div');
  contentDiv.className = 'feed-content-share-content';
  const contentInner = document.createElement('div');
  contentInner.className = 'feed-content-share-content-inner';

  const buttonA = document.createElement('a');
  buttonA.href = detailUrl;
  buttonA.className = 'feed-content-share-button';
  buttonA.textContent = 'VIEW POST';

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'feed-content-share-button-container';
  buttonContainer.appendChild(buttonA);

  if (title && title.trim()) {
    const titleP = document.createElement('p');
    titleP.className = 'feed-content-share-title';
    titleP.title = title;
    titleP.textContent = title;
    contentInner.appendChild(titleP);
  }
  if (showDescription) {
    const descP = document.createElement('p');
    descP.className = 'feed-content-share-description';
    descP.title = description;
    descP.textContent = truncatedDescription;
    contentInner.appendChild(descP);
  }
  contentInner.appendChild(buttonContainer);
  contentDiv.appendChild(contentInner);
  container.appendChild(contentDiv);

  return container;
}

function buildEventAddressLine(data: EventTicketShareData): string {
  const parts: string[] = [];
  if (data.venueName?.trim()) parts.push(data.venueName.trim());
  const locationParts: string[] = [];
  if (data.city?.trim()) locationParts.push(data.city.trim());
  if (data.state?.trim()) locationParts.push(data.state.trim());
  if (data.country?.trim()) {
    const c = data.country.trim();
    if (c.toLowerCase() !== 'united states') locationParts.push(c);
  }
  if (locationParts.length) parts.push(locationParts.join(', '));
  return parts.join(' - ');
}

function formatTimeTo12Hour(time24: string): string {
  if (!time24) return '';
  if (time24.split(' ').length === 2) return time24;
  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutesFormatted = minutes < 10 ? '0' + minutes : String(minutes);
  return `${hours}:${minutesFormatted} ${ampm}`.trim();
}

function getFormattedDateTime(data: EventTicketShareData): string {
  if (!data?.eventDate && !data?.eventTime) return '';
  return formatEventDateTimeLine(data.eventDate, data.eventTime, formatTimeTo12Hour);
}

function buildEventContentBlock(data: EventTicketShareData, title: string, detailUrl: string): HTMLElement {
  const contentDiv = document.createElement('div');
  contentDiv.className = 'event-ticket-share-content';

  const contentInner = document.createElement('div');
  contentInner.className = 'event-ticket-share-content-inner';

  if (title && title.trim()) {
    const titleP = document.createElement('p');
    titleP.className = 'event-ticket-share-title';
    titleP.title = title;
    titleP.textContent = title;
    contentInner.appendChild(titleP);
  }

  const addressLine = buildEventAddressLine(data);
  if (addressLine) {
    const addressDiv = document.createElement('div');
    addressDiv.className = 'event-ticket-share-meta-item';
    addressDiv.setAttribute('data-skip-sanitize', 'true');
    const addressSpan = document.createElement('span');
    addressSpan.textContent = addressLine;
    addressDiv.appendChild(addressSpan);
    contentInner.appendChild(addressDiv);
  }

  const dateTimeLine = getFormattedDateTime(data);
  if (dateTimeLine) {
    const dateDiv = document.createElement('div');
    dateDiv.className = 'event-ticket-share-meta-item';
    dateDiv.setAttribute('data-skip-sanitize', 'true');
    const dateSpan = document.createElement('span');
    dateSpan.textContent = `${dateTimeLine} ${data.timezone ? `(${data.timezone})` : ''}`.trim();
    dateDiv.appendChild(dateSpan);
    contentInner.appendChild(dateDiv);
  }

  const buttonA = document.createElement('a');
  buttonA.href = detailUrl;
  buttonA.className = 'event-ticket-share-button';
  buttonA.textContent = 'VIEW EVENT';

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'event-ticket-share-button-container';
  buttonContainer.appendChild(buttonA);
  contentInner.appendChild(buttonContainer);

  contentDiv.appendChild(contentInner);
  return contentDiv;
}

export function populateEventTicketSharePlaceholder(
  data: EventTicketShareData,
  node?: HTMLElement
): HTMLElement {
  const { image, detailUrl, eventDate, venueName, eventSk } = data;
  const title = decodeHtmlEntities(data.title ?? '');

  const container = node || document.createElement('div');
  container.setAttribute('contenteditable', 'false');
  container.classList.add('event-ticket-share-placeholder', 'event-ticket-share-card-table');
  container.setAttribute('data-image', image || '');
  container.setAttribute('data-title', title);
  container.setAttribute('data-detail-url', detailUrl);
  container.setAttribute('data-event-date', eventDate || '');
  container.setAttribute('data-event-time', data.eventTime || '');
  container.setAttribute('data-timezone', data.timezone || '');
  container.setAttribute('data-venue-name', venueName || '');
  container.setAttribute('data-city', data.city || '');
  container.setAttribute('data-state', data.state || '');
  container.setAttribute('data-country', data.country || '');
  container.setAttribute('data-event-sk', eventSk || '');

  const imageDiv = document.createElement('div');
  imageDiv.className = 'event-ticket-share-image';
  if(image) {
    const img = document.createElement('img');
    img.src = image;
    img.alt = title;
    imageDiv.appendChild(img);
  }

  container.appendChild(imageDiv);
  container.appendChild(buildEventContentBlock(data, title, detailUrl));

  return container;
}

export function processEventTicketShareForEmail(html: string): string {
  if (!html) return html;
  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const placeholders = doc.querySelectorAll('.event-ticket-share-placeholder');
  let configThemeColor = 'linear-gradient(95deg,#1de9b6 0%,#00b6ea 100%)';
  let configButtonTextColor = '#000';
  if (!!environment.whiteLabelConfig) {
    const root = document.documentElement;
    const computedStyles = getComputedStyle(root);
    configButtonTextColor = computedStyles.getPropertyValue('--configButtonTextColor').trim();
    configThemeColor = computedStyles.getPropertyValue('--configThemeColor').trim();
  }
  placeholders.forEach(placeholder => {
    const imageSrc = escapeHtml(placeholder.getAttribute('data-image') || '');
    const title = escapeHtml(placeholder.getAttribute('data-title') || '');
    const detailUrl = escapeHtml(placeholder.getAttribute('data-detail-url') || '#');
    const eventDate = placeholder.getAttribute('data-event-date') || '';
    const eventTime = placeholder.getAttribute('data-event-time') || '';
    const timezone = placeholder.getAttribute('data-timezone') || '';
    const venueName = placeholder.getAttribute('data-venue-name') || '';
    const city = placeholder.getAttribute('data-city') || '';
    const state = placeholder.getAttribute('data-state') || '';
    const country = placeholder.getAttribute('data-country') || '';
    const eventSk = escapeHtml(placeholder.getAttribute('data-event-sk') || '');

    const addressLine = buildEventAddressLine({ venueName, city, state, country } as EventTicketShareData);
    const escapedAddress = escapeHtml(addressLine);

    const dateTimeLine = getFormattedDateTime({ eventDate, eventTime } as EventTicketShareData);
    const timezoneSuffix = timezone ? ` (${escapeHtml(timezone)})` : '';
    const escapedDateTime = escapeHtml(dateTimeLine) + timezoneSuffix;

    const addressBlock = escapedAddress
      ? `<p data-skip-sanitize="true" style="font-size:14px;color:#000;line-height:1.4;" class="event-ticket-share-address">
                    ${escapedAddress}
                  </p>`
      : '';

    const dateBlock = escapedDateTime.trim()
      ? `<p data-skip-sanitize="true" style="font-size:14px;color:#000;margin:0px 0px 16px 0px !important;line-height:1.4;" class="event-ticket-share-datetime">
                    ${escapedDateTime.trim()}
                  </p>`
      : '';

    const desktopHtml = `<table data-skip-sanitize="true" class="event-ticket-share-card-table" role="presentation" cellpadding="0" cellspacing="0" border="0" style="display: table; border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody style="display: block;" data-skip-sanitize="true">
        <tr>
          ${
            imageSrc
              ? `
                <td data-skip-sanitize="true"
                  class="event-ticket-share-image"
                  style="display: block; width: 100%; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">

                  <img
                    src="${imageSrc}"
                    alt="${title}"
                    style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;"
                    data-skip-sanitize="true" />
                </td>
              `
              : ''
          }
          <td data-skip-sanitize="true" class="event-ticket-share-content" style="vertical-align: top; height: auto; padding-left: 16px !important;">
            <table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  <p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;line-height:1;" title="${title}" class="event-ticket-share-title">
                    ${title}
                  </p>
                  ${addressBlock}
                  ${dateBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="event-ticket-share-button-container">
                  <a data-skip-sanitize="true" class="event-ticket-share-button" href="${detailUrl}" style="display:inline-block;padding:15px 24px;border-radius:28px;background: ${configThemeColor};color: ${configButtonTextColor} !important;text-decoration:none;font-weight:700;font-size:14px;">
                    <div style="color: ${configButtonTextColor} !important;">VIEW EVENT</div>
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>`;

    const mobileHtml = `<table data-skip-sanitize="true" class="event-ticket-share-card-table-mobile" role="presentation" cellpadding="0" cellspacing="0" border="0" style="display: none; border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody data-skip-sanitize="true" style="display: block;">
        ${
      imageSrc
        ? `
        <tr>
          <td data-skip-sanitize="true"
            class="event-ticket-share-image"
            style="display: block; width: 100%; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">

            <img
              src="${imageSrc}"
              alt="${title}"
              style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;"
              data-skip-sanitize="true" />
          </td>
        </tr>
        `
        : ''
    }
        <tr>
          <td data-skip-sanitize="true" class="event-ticket-share-content" style="vertical-align: top; height: auto;">
            <table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  <p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;line-height:1;" title="${title}" class="event-ticket-share-title">
                    ${title}
                  </p>
                  ${addressBlock}
                  ${dateBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="event-ticket-share-button-container">
                  <a data-skip-sanitize="true" class="event-ticket-share-button" href="${detailUrl}" style="display:inline-block;padding:15px 24px;border-radius:28px;background: ${configThemeColor};color: ${configButtonTextColor} !important;text-decoration:none;font-weight:700;font-size:14px;">
                    <div style="color: ${configButtonTextColor} !important;">VIEW EVENT</div>
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>`;

    const template = document.createElement('template');
    template.innerHTML = `<div class="event-ticket-share-wrapper-event" data-event-sk="${eventSk}" data-image="${imageSrc}" data-title="${title}" data-detail-url="${detailUrl}" data-event-date="${escapeHtml(eventDate)}" data-event-time="${escapeHtml(eventTime)}" data-timezone="${escapeHtml(timezone)}" data-venue-name="${escapeHtml(venueName)}" data-city="${escapeHtml(city)}" data-state="${escapeHtml(state)}" data-country="${escapeHtml(country)}">${collapseTableHtmlWhitespace(desktopHtml)}${collapseTableHtmlWhitespace(mobileHtml)}</div>`;
    placeholder.replaceWith(template.content.firstChild!);
  });

  return doc.body.innerHTML;
}

export function reverseEventTicketShareFromEmail(html: string): string {
  if (!html) return html;
  if (typeof DOMParser === 'undefined') return html;

  const placeholderRegex = new RegExp(Application.NEWLINE_PLACEHOLDER, 'g');
  const preservedHtml = html.replace(/\n/g, Application.NEWLINE_PLACEHOLDER);

  const parser = new DOMParser();
  const doc = parser.parseFromString(preservedHtml, 'text/html');
  const wrappers = doc.querySelectorAll('.event-ticket-share-wrapper-event');

  wrappers.forEach(wrapper => {
    const desktopTable = wrapper.querySelector('.event-ticket-share-card-table');
    if (!desktopTable) return;

    const img = desktopTable.querySelector('.event-ticket-share-image img') as HTMLImageElement;
    const imageSrc = img?.getAttribute('src') || '';

    const titleP = desktopTable.querySelector('.event-ticket-share-title') as HTMLElement;
    const title = (titleP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const anchor = desktopTable.querySelector('.event-ticket-share-button') as HTMLAnchorElement;
    const detailUrl = anchor?.getAttribute('href') || '#';

    const eventSk = wrapper.getAttribute('data-event-sk') || '';
    const eventDate = (wrapper.getAttribute('data-event-date') || '').replace(placeholderRegex, '\n');
    const eventTime = (wrapper.getAttribute('data-event-time') || '').replace(placeholderRegex, '\n');
    const timezone = (wrapper.getAttribute('data-timezone') || '').replace(placeholderRegex, '\n');
    const venueName = (wrapper.getAttribute('data-venue-name') || '').replace(placeholderRegex, '\n');
    const city = (wrapper.getAttribute('data-city') || '').replace(placeholderRegex, '\n');
    const state = (wrapper.getAttribute('data-state') || '').replace(placeholderRegex, '\n');
    const country = (wrapper.getAttribute('data-country') || '').replace(placeholderRegex, '\n');

    const placeholder = populateEventTicketSharePlaceholder({
      image: imageSrc,
      title,
      detailUrl,
      eventDate,
      eventTime,
      timezone,
      venueName,
      city,
      state,
      country,
      eventSk
    });

    wrapper.replaceWith(placeholder);
  });

  return doc.body.innerHTML.replace(placeholderRegex, '<br>');
}

/**
 * Collapses newlines and extra whitespace between HTML tags only.
 * Prevents downstream newline-to-br conversion from inserting <br/> tags between table elements.
 * Does not affect content inside tags (e.g. attribute values like src, or text content).
 */
function collapseTableHtmlWhitespace(html: string): string {
  return html.replace(/>\s+</g, '><').trim();
}

/**
 * Process HTML content to replace store share placeholders with email-safe HTML.
 * Call this before sending content via HTTP request.
 * @param html - The HTML content from Quill editor
 * @returns Processed HTML with email-safe store share cards
 */
export function processStoreShareForEmail(html: string): string {
  if (!html) return html;

  // Check if DOMParser and document are available (browser environment)
  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
    // In SSR environment, return HTML as-is
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const placeholders = doc.querySelectorAll('.store-share-placeholder');

  placeholders.forEach(placeholder => {
    const imageSrc = escapeHtml(placeholder.getAttribute('data-image') || '');
    const title = escapeHtml(placeholder.getAttribute('data-title') || '');
    const detailUrl = escapeHtml(placeholder.getAttribute('data-detail-url') || '#');

    const rawDescription = placeholder.getAttribute('data-description') || '';
    const fullDescriptionForTitle = escapeHtml(htmlToPlainTextForTitle(rawDescription));
    const truncatedDescription = truncateHtmlDescription(rawDescription);
    const hasDescription = !!(truncatedDescription && truncatedDescription.trim());
    const descriptionBlock = hasDescription
      ? `<p data-skip-sanitize="true" style="font-size:14px;color:#000;margin:0px 0px 16px 0px !important;line-height:1.4;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;" title="${fullDescriptionForTitle}" class="store-share-description">
                    ${truncatedDescription}
                  </p>`
      : '';

    const desktopHtml = `<table data-skip-sanitize="true" class="store-share-card-table" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody style="display: block;" data-skip-sanitize="true">
        <tr>
          <td data-skip-sanitize="true" class="store-share-image" style="display: block; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">
            <img src="${imageSrc}" alt="${title}" height="auto" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;" data-skip-sanitize="true" />
          </td>
          <td data-skip-sanitize="true" class="store-share-content" style="vertical-align: top; height: auto; padding-left: 16px !important;">
            <table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  <p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;margin-bottom:16px !important;line-height:1;" title="${title}" class="store-share-title">
                    ${title}
                  </p>
                  ${descriptionBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="store-share-button-container">
                  <a data-skip-sanitize="true" class="store-share-button" href="${detailUrl}" style="display:inline-block;padding:20px 24px;border-radius:28px;background:linear-gradient(95deg,#1de9b6 0%,#00b6ea 100%);color:#000;text-decoration:none;font-weight:700;font-size:14px;">
                    BUY NOW
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>`;

    const mobileHtml = `<table data-skip-sanitize="true" class="store-share-card-table-mobile" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody data-skip-sanitize="true" style="display: block;">
        <tr>
          <td data-skip-sanitize="true" class="store-share-image" style="display: block; width: 100%; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">
            <img src="${imageSrc}" alt="${title}" height="auto" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;" data-skip-sanitize="true" />
          </td>
        </tr>
        <tr>
          <td data-skip-sanitize="true" class="store-share-content" style="vertical-align: top; height: auto;">
            <table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  <p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;margin-bottom:16px !important;line-height:1;" title="${title}" class="store-share-title">
                    ${title}
                  </p>
                  ${descriptionBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="store-share-button-container">
                  <a data-skip-sanitize="true" class="store-share-button" href="${detailUrl}" style="display:inline-block;padding:20px 24px;border-radius:28px;background:linear-gradient(95deg,#1de9b6 0%,#00b6ea 100%);color:#000;text-decoration:none;font-weight:700;font-size:14px;">
                    BUY NOW
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>`;

    const template = document.createElement('template');
    template.innerHTML = `<div class="store-share-wrapper">${collapseTableHtmlWhitespace(desktopHtml)}${collapseTableHtmlWhitespace(mobileHtml)}</div>`;
    placeholder.replaceWith(template.content.firstChild!);
  });

  return doc.body.innerHTML;
}

/**
 * Reverse of processStoreShareForEmail - converts email-compatible store share cards
 * back to editor placeholder format for editing
 * @param html - HTML string with email-compatible store share cards
 * @returns HTML with editor-compatible store share placeholders
 */
export function reverseStoreShareFromEmail(html: string): string {
  if (!html) return html;

  // Check if DOMParser is available (browser environment)
  if (typeof DOMParser === 'undefined') {
    // In SSR environment, return HTML as-is
    return html;
  }

  // Preserve newlines by converting to HTML comment placeholder before parsing
  // DOMParser normalizes whitespace, so we need to protect newlines
  const placeholderRegex = new RegExp(Application.NEWLINE_PLACEHOLDER, 'g');
  const preservedHtml = html.replace(/\n/g, Application.NEWLINE_PLACEHOLDER);

  const parser = new DOMParser();
  const doc = parser.parseFromString(preservedHtml, 'text/html');
  const wrappers = doc.querySelectorAll('.store-share-wrapper');

  wrappers.forEach(wrapper => {
    const desktopTable = wrapper.querySelector('.store-share-card-table');
    if (!desktopTable) return;

    const img = desktopTable.querySelector('.store-share-image img') as HTMLImageElement;
    const imageSrc = img?.getAttribute('src') || '';

    const titleP = desktopTable.querySelector('.store-share-content .store-share-title') as HTMLElement;
    const title = (titleP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const descriptionP = desktopTable.querySelector('.store-share-content .store-share-description') as HTMLElement;
    const description = (descriptionP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const anchor = desktopTable.querySelector('.store-share-content a') as HTMLAnchorElement;
    const detailUrl = anchor?.getAttribute('href') || '#';

    const placeholder = populateStoreSharePlaceholder({ image: imageSrc, title, description, detailUrl });

    wrapper.replaceWith(placeholder);
  });

  return doc.body.innerHTML.replace(placeholderRegex, '<br>');
}

/**
 * Process HTML content to replace portfolio share placeholders with email-safe HTML.
 * Call this before sending content via HTTP request.
 * @param html - The HTML content from Quill editor
 * @returns Processed HTML with email-safe portfolio share cards
 */
export function processPortfolioShareForEmail(html: string): string {
  if (!html) return html;

  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const placeholders = doc.querySelectorAll('.portfolio-share-placeholder');

  placeholders.forEach(placeholder => {
    const imageSrc = escapeHtml(placeholder.getAttribute('data-image') || '');
    const title = escapeHtml(placeholder.getAttribute('data-title') || '');
    const detailUrl = escapeHtml(placeholder.getAttribute('data-detail-url') || '#');
    const portfolioType = (placeholder.getAttribute('data-portfolio-type') || '').toUpperCase();
    const isIndividual = placeholder.getAttribute('data-is-individual') !== 'false';
    const buttonText = getPortfolioCtaLabel(portfolioType, isIndividual);

    const rawDescription = placeholder.getAttribute('data-description') || '';
    const fullDescriptionForTitle = escapeHtml(htmlToPlainTextForTitle(rawDescription));
    const truncatedDescription = truncateHtmlDescription(rawDescription);
    const hasDescription = !!(truncatedDescription && truncatedDescription.trim());
    const descriptionBlock = hasDescription
      ? `<p data-skip-sanitize="true" style="font-size:14px;color:#000;margin:0px 0px 16px 0px !important;line-height:1.4;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;" title="${fullDescriptionForTitle}" class="portfolio-share-description">
                    ${truncatedDescription}
                  </p>`
      : '';

    const desktopHtml = `<table data-skip-sanitize="true" class="portfolio-share-card-table" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody style="display: block;" data-skip-sanitize="true">
        <tr>
          <td data-skip-sanitize="true" class="portfolio-share-image" style="display: block; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">
            <img src="${imageSrc}" alt="${title}" height="auto" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;" data-skip-sanitize="true" />
          </td>
          <td data-skip-sanitize="true" class="portfolio-share-content" style="vertical-align: top; height: auto; padding-left: 16px !important;">
            <table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  <p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;margin-bottom:16px !important;line-height:1;" title="${title}" class="portfolio-share-title">
                    ${title}
                  </p>
                  ${descriptionBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="portfolio-share-button-container">
                  <a data-skip-sanitize="true" class="portfolio-share-button" href="${detailUrl}" style="display:inline-block;padding:20px 24px;border-radius:28px;background:linear-gradient(95deg,#1de9b6 0%,#00b6ea 100%);color:#000;text-decoration:none;font-weight:700;font-size:14px;">
                    ${buttonText}
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>`;

    const mobileHtml = `<table data-skip-sanitize="true" class="portfolio-share-card-table-mobile" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody data-skip-sanitize="true" style="display: block;">
        <tr>
          <td data-skip-sanitize="true" class="portfolio-share-image" style="display: block; width: 100%; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">
            <img src="${imageSrc}" alt="${title}" height="auto" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;" data-skip-sanitize="true" />
          </td>
        </tr>
        <tr>
          <td data-skip-sanitize="true" class="portfolio-share-content" style="vertical-align: top; height: auto;">
            <table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  <p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;margin-bottom:16px !important;line-height:1;" title="${title}" class="portfolio-share-title">
                    ${title}
                  </p>
                  ${descriptionBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="portfolio-share-button-container">
                  <a data-skip-sanitize="true" class="portfolio-share-button" href="${detailUrl}" style="display:inline-block;padding:20px 24px;border-radius:28px;background:linear-gradient(95deg,#1de9b6 0%,#00b6ea 100%);color:#000;text-decoration:none;font-weight:700;font-size:14px;">
                    ${buttonText}
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>`;

    const template = document.createElement('template');
    template.innerHTML = `<div class="portfolio-share-wrapper-portfolio" data-portfolio-type="${escapeHtml(portfolioType)}" data-is-individual="${isIndividual}">${collapseTableHtmlWhitespace(desktopHtml)}${collapseTableHtmlWhitespace(mobileHtml)}</div>`;
    placeholder.replaceWith(template.content.firstChild!);
  });

  return doc.body.innerHTML;
}

/**
 * Reverse of processPortfolioShareForEmail - converts email-compatible portfolio share cards
 * back to editor placeholder format for editing
 * @param html - HTML string with email-compatible portfolio share cards
 * @returns HTML with editor-compatible portfolio share placeholders
 */
export function reversePortfolioShareFromEmail(html: string): string {
  if (!html) return html;

  if (typeof DOMParser === 'undefined') {
    return html;
  }

  const placeholderRegex = new RegExp(Application.NEWLINE_PLACEHOLDER, 'g');
  const preservedHtml = html.replace(/\n/g, Application.NEWLINE_PLACEHOLDER);

  const parser = new DOMParser();
  const doc = parser.parseFromString(preservedHtml, 'text/html');
  const wrappers = doc.querySelectorAll('.portfolio-share-wrapper-portfolio');

  wrappers.forEach(wrapper => {
    const desktopTable = wrapper.querySelector('.portfolio-share-card-table');
    if (!desktopTable) return;

    const img = desktopTable.querySelector('.portfolio-share-image img') as HTMLImageElement;
    const imageSrc = img?.getAttribute('src') || '';

    const titleP = desktopTable.querySelector('.portfolio-share-content p[title]') as HTMLElement;
    const title = (titleP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const descriptionP = desktopTable.querySelectorAll('.portfolio-share-content p[title]')[1] as HTMLElement;
    const description = (descriptionP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const anchor = desktopTable.querySelector('.portfolio-share-content a') as HTMLAnchorElement;
    const detailUrl = anchor?.getAttribute('href') || '#';

    const buttonText = anchor?.textContent?.trim() || 'View Now';
    let portfolioType = (wrapper.getAttribute('data-portfolio-type') || '').toUpperCase();
    let isIndividual = wrapper.getAttribute('data-is-individual') !== 'false';
    if (!portfolioType) {
      const resolved = PORTFOLIO_CTA_BUTTON_TO_TYPE[buttonText];
      if (resolved) {
        portfolioType = resolved.portfolioType;
        isIndividual = resolved.isIndividual;
      } else {
        portfolioType = 'CUSTOM';
      }
    }

    const placeholder = populatePortfolioSharePlaceholder({
      image: imageSrc,
      title,
      description,
      detailUrl,
      portfolioType,
      isIndividual
    });

    wrapper.replaceWith(placeholder);
  });

  return doc.body.innerHTML.replace(placeholderRegex, '<br>');
}

/**
 * Process HTML content to replace feed content share placeholders with email-safe HTML.
 */
export function processFeedContentShareForEmail(html: string): string {
  if (!html) return html;
  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const placeholders = doc.querySelectorAll('.feed-content-share-placeholder');

  placeholders.forEach(placeholder => {
    const imageSrc = (placeholder.getAttribute('data-image') || '').trim();
    const hasImage = !!imageSrc;
    const escapedImageSrc = escapeHtml(imageSrc);
    const rawTitle = placeholder.getAttribute('data-title') || '';
    const title = escapeHtml(rawTitle);
    const hasTitle = !!(rawTitle && rawTitle.trim());
    const detailUrl = escapeHtml(placeholder.getAttribute('data-detail-url') || '#');
    const postType = escapeHtml(placeholder.getAttribute('data-post-type') || '');
    const postSk = escapeHtml(placeholder.getAttribute('data-post-sk') || '');

    const rawDescription = placeholder.getAttribute('data-description') || '';
    const fullDescriptionForTitle = escapeHtml(htmlToPlainTextForTitle(rawDescription));
    const truncatedDescription = truncateHtmlDescription(rawDescription);
    const hasDescription = !!(truncatedDescription && truncatedDescription.trim());
    const isArticle = (postType || '').toLowerCase() === 'article';
    const showDescription = !isArticle && hasDescription;

    const titleBlock = hasTitle
      ? `<p data-skip-sanitize="true" style="font-size:20px;font-weight:700;color:#000;margin-bottom:16px !important;line-height:1;" title="${title}" class="feed-content-share-title">
                    ${title}
                  </p>`
      : '';

    const descriptionBlock = showDescription
      ? `<p data-skip-sanitize="true" style="font-size:14px;color:#000;margin:0px 0px 16px 0px !important;line-height:1.4;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;" title="${fullDescriptionForTitle}" class="feed-content-share-description">
                    ${truncatedDescription}
                  </p>`
      : '';

    const contentBlockHtml = `<table data-skip-sanitize="true" border="0" cellpadding="0" cellspacing="0" height="100%" style="padding: 16px 0;">
              <tr>
                <td data-skip-sanitize="true" style="vertical-align:top;">
                  ${titleBlock}
                  ${descriptionBlock}
                </td>
              </tr>
              <tr>
                <td data-skip-sanitize="true" class="feed-content-share-button-container">
                  <a data-skip-sanitize="true" class="feed-content-share-button" href="${detailUrl}" style="display:inline-block;padding:20px 24px;border-radius:28px;background:linear-gradient(95deg,#1de9b6 0%,#00b6ea 100%);color:#000;text-decoration:none;font-weight:700;font-size:14px;">
                    VIEW POST
                  </a>
                </td>
              </tr>
            </table>`;

    let desktopHtml: string;
    let mobileHtml: string;

    if (hasImage) {
      desktopHtml = `<table data-skip-sanitize="true" class="feed-content-share-card-table" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody style="display: block;" data-skip-sanitize="true">
        <tr>
          <td data-skip-sanitize="true" class="feed-content-share-image" style="display: block; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">
            <img src="${escapedImageSrc}" alt="${title}" height="auto" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;" data-skip-sanitize="true" />
          </td>
          <td data-skip-sanitize="true" class="feed-content-share-content" style="vertical-align: top; height: auto; padding-left: 16px !important;">
            ${contentBlockHtml}
          </td>
        </tr>
      </tbody>
    </table>`;

      mobileHtml = `<table data-skip-sanitize="true" class="feed-content-share-card-table-mobile" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody data-skip-sanitize="true" style="display: block;">
        <tr>
          <td data-skip-sanitize="true" class="feed-content-share-image" style="display: block; width: 100%; height: auto; vertical-align: middle; border-radius: ${StoreShareConstants.BORDER_RADIUS}px !important; overflow: hidden;">
            <img src="${escapedImageSrc}" alt="${title}" height="auto" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; width: fit-content; max-width: ${StoreShareConstants.IMAGE_MAX_WIDTH}px; height: auto;" data-skip-sanitize="true" />
          </td>
        </tr>
        <tr>
          <td data-skip-sanitize="true" class="feed-content-share-content" style="vertical-align: top;">
            ${contentBlockHtml}
          </td>
        </tr>
      </tbody>
    </table>`;
    } else {
      desktopHtml = `<table data-skip-sanitize="true" class="feed-content-share-card-table feed-content-share-no-image" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody style="display: block;" data-skip-sanitize="true">
        <tr>
          <td data-skip-sanitize="true" class="feed-content-share-content" style="vertical-align: top; padding: 16px 0;">
            ${contentBlockHtml}
          </td>
        </tr>
      </tbody>
    </table>`;

      mobileHtml = `<table data-skip-sanitize="true" class="feed-content-share-card-table-mobile feed-content-share-no-image" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-radius: ${StoreShareConstants.BORDER_RADIUS}px; border-collapse: separate; width: 100%; max-width: 100%; line-height: 0;">
      <tbody data-skip-sanitize="true" style="display: block;">
        <tr>
          <td data-skip-sanitize="true" class="feed-content-share-content" style="vertical-align: top; padding: 16px 0;">
            ${contentBlockHtml}
          </td>
        </tr>
      </tbody>
    </table>`;
    }

    const template = document.createElement('template');
    template.innerHTML = `<div class="feed-content-share-wrapper-feed" data-post-type="${postType}" data-post-sk="${postSk}">${collapseTableHtmlWhitespace(desktopHtml)}${collapseTableHtmlWhitespace(mobileHtml)}</div>`;
    placeholder.replaceWith(template.content.firstChild!);
  });

  return doc.body.innerHTML;
}

/**
 * Reverse of processFeedContentShareForEmail - converts email-compatible feed content share cards
 * back to editor placeholder format for editing.
 */
export function reverseFeedContentShareFromEmail(html: string): string {
  if (!html) return html;
  if (typeof DOMParser === 'undefined') return html;

  const placeholderRegex = new RegExp(Application.NEWLINE_PLACEHOLDER, 'g');
  const preservedHtml = html.replace(/\n/g, Application.NEWLINE_PLACEHOLDER);

  const parser = new DOMParser();
  const doc = parser.parseFromString(preservedHtml, 'text/html');
  const wrappers = doc.querySelectorAll('.feed-content-share-wrapper-feed');

  wrappers.forEach(wrapper => {
    const desktopTable = wrapper.querySelector('.feed-content-share-card-table');
    if (!desktopTable) return;

    const img = desktopTable.querySelector('.feed-content-share-image img') as HTMLImageElement;
    const imageSrc = img?.getAttribute('src') || '';

    const titleP = desktopTable.querySelector('.feed-content-share-content .feed-content-share-title') as HTMLElement;
    const title = (titleP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const descriptionP = desktopTable.querySelector('.feed-content-share-content .feed-content-share-description') as HTMLElement;
    const description = (descriptionP?.getAttribute('title') || '').replace(placeholderRegex, '\n');

    const anchor = desktopTable.querySelector('.feed-content-share-content a') as HTMLAnchorElement;
    const detailUrl = anchor?.getAttribute('href') || '#';

    const postType = wrapper.getAttribute('data-post-type') || '';
    const postSk = wrapper.getAttribute('data-post-sk') || '';

    const placeholder = populateFeedContentSharePlaceholder({
      image: imageSrc,
      title,
      description,
      detailUrl,
      postType,
      postSk
    });

    wrapper.replaceWith(placeholder);
  });

  return doc.body.innerHTML.replace(placeholderRegex, '<br>');
}

/**
   * Navigate to store product detail page based on product type or return URL
   * @param productValue - Product object with product_type, pk, and sk
   * @param user - User object with user_name and gsi4pk
   * @returns boolean | string - Returns true if navigation was successful, false if failed, or URL string if returnUrlOnly is true
   */
export const getStoreProductURL = (productValue: any, user: any): string => {
  if (!productValue || !user) {
    return '';
  }

  let pk = '';
  let sk = '';
  let url = '';

  if ([STORE_MONETIZE_TYPE.merchandise, STORE_MONETIZE_TYPE.nfts, STORE_MONETIZE_TYPE.nft].includes(productValue?.product?.product_type)) {
    pk = productValue.pk?.replace(BIO_PRODUCT_PREFIXES.USER, '') || '';
    sk = productValue.sk?.replace(BIO_PRODUCT_PREFIXES.USER, '') || '';
    const merchSk = sk?.split(BIO_PRODUCT_PREFIXES.HASH)?.[1];
    if (productValue?.product?.product_type === STORE_MONETIZE_TYPE.merchandise) {
      url = `${BIO_URL_SEGMENTS.DASHBOARD_MERCHANDISE_DETAIL}/${user?.user_name}/${encodeURIComponent(merchSk)}/${BIO_URL_SEGMENTS.STORE}/${STORE_MONETIZE_TYPE.merchandise}`;
    } else {
      url = `${BIO_URL_SEGMENTS.DASHBOARD_MERCHANDISE_DETAIL}/${user?.user_name}/${encodeURIComponent(merchSk)}/${BIO_URL_SEGMENTS.STORE}/${STORE_MONETIZE_TYPE.nft}`;
    }
  } else if (productValue?.product?.product_type === STORE_MONETIZE_TYPE.downloadables) {
    const downloadableId = productValue.sk?.split(BIO_PRODUCT_PREFIXES.HASH)?.[1];
    url = `${BIO_URL_SEGMENTS.DASHBOARD_MERCHANDISE_DETAILS}/${user?.user_name}&${user?.gsi4pk}/${BIO_URL_SEGMENTS.DOWNLOADABLE}/${downloadableId}/${BIO_URL_SEGMENTS.DOWNLOADABLES_UPPER}`;
  } else {
    pk = productValue.pk?.replace(BIO_PRODUCT_PREFIXES.USER, '') || '';
    sk = productValue.sk?.replace(BIO_PRODUCT_PREFIXES.USER, '') || '';
    url = `${BIO_URL_SEGMENTS.DASHBOARD_MERCHANDISE_DETAILS}/${user?.user_name}&${user?.gsi4pk}/${encodeURIComponent(pk)}/${encodeURIComponent(sk)}/${BIO_URL_SEGMENTS.STORE}`;
  }

  return url;
}

/**
 * Cleans Shopify API HTML descriptions by extracting text content from MsoNormal p tags.
 * Returns plain text with newlines between paragraphs.
 * @param html - HTML string from Shopify API that may contain MsoNormal tags
 * @returns Plain text with newlines between paragraphs
 */
export function cleanShopifyDescription(html: string): string {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const paragraphs = Array.from(doc.querySelectorAll("p.MsoNormal"));

  if (paragraphs.length > 0) {
    return paragraphs
      .map((node: HTMLElement) => node.textContent?.trim() || '')
      .filter(text => text.length > 0)
      .join('\n');
  }

  return doc.body.textContent?.trim() || html;
}

/**
 * Validates decimal number input.
 * Only allows numbers and one decimal point (can be placed anywhere).
 * Prevents copy/paste and invalid characters from being entered.
 */
export function validateDecimalNumberInput(event: KeyboardEvent): void {
  const specialKeys = [
    'Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight',
    'ArrowUp', 'ArrowDown', 'Delete', 'Enter', 'Escape'
  ];

  if (specialKeys.includes(event.key)) return;

  if ((event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'v' || event.key === 'x' || event.key === 'a')) {
    event.preventDefault();
    return;
  }

  if (event.ctrlKey || event.metaKey || event.altKey) {
    if (!event.altKey) {
      return;
    }
  }

  const target = event.target as HTMLInputElement;
  if (!target) return;

  const current = target.value || '';
  const position = target.selectionStart ?? 0;
  const selectionEnd = target.selectionEnd ?? position;

  let key = event.key;

  if (
    key === 'Decimal' ||
    key === 'Period' ||
    key === '.' ||
    event.code === 'NumpadDecimal' ||
    event.code === 'Period'
  ) {
    key = '.';
  }

  const isDigit = /^[0-9]$/.test(key);
  const isDot = key === '.';

  if (!isDigit && !isDot) {
    event.preventDefault();
    return;
  }

  // If it's a dot, check if one already exists
  if (isDot) {
    if (current.includes('.')) {
      event.preventDefault();
      return;
    }
  }

  // Build the next value to validate
  const nextValue =
    current.slice(0, position) + key + current.slice(selectionEnd);

  // Validate: only digits and at most one decimal point
  const decimalRegex = /^\d*\.?\d*$/;

  if (!decimalRegex.test(nextValue)) {
    event.preventDefault();
  }
}

export function formatDuration(seconds: number): string {
  const date = new Date(seconds * 1000);

  const h = date.getUTCHours();
  const m = String(date.getUTCMinutes()).padStart(2, '0');
  const s = String(date.getUTCSeconds()).padStart(2, '0');

  return h > 0
    ? `${String(h).padStart(2, '0')}:${m}:${s}`
    : `${m}:${s}`;
}

/** Primary = `{...}` gradient; secondary = `[...]` gradient (marketing headings). */
export const HEADING_HIGHLIGHT_VARIANTS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary'
} as const;

export type HeadingHighlightVariant =
  (typeof HEADING_HIGHLIGHT_VARIANTS)[keyof typeof HEADING_HIGHLIGHT_VARIANTS];

/**
 * Segment with text and highlight flag for heading display
 */
export interface HeadingSegment {
  text: string;
  highlight: boolean;
  highlightVariant?: HeadingHighlightVariant;
}

/**
 * Splits a heading by `{...}` (primary highlight) and `[...]` (secondary highlight).
 * Uses the raw string for splitting (leading/trailing newlines and spaces are preserved);
 * only all-whitespace headings yield an empty list. Used by marketing layout components.
 */
export function getHeadingSegments(heading: string): HeadingSegment[] {
  const raw = heading ?? '';
  if (raw.trim() === '') return [];

  const segments: HeadingSegment[] = [];
  const regex = /(\{([^}]*)\}|\[([^\]]*)\])/g;
  let lastIndex = Application.ZERO;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        text: raw.slice(lastIndex, match.index),
        highlight: false
      });
    }
    const isSecondaryHighlight = match[Application.ONE]?.startsWith('[');
    segments.push({
      text: match[2] ?? match[3] ?? '',
      highlight: true,
      highlightVariant: isSecondaryHighlight
        ? HEADING_HIGHLIGHT_VARIANTS.SECONDARY
        : HEADING_HIGHLIGHT_VARIANTS.PRIMARY
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < raw.length) {
    segments.push({ text: raw.slice(lastIndex), highlight: false });
  }
  if (!segments.length) {
    return [{ text: raw, highlight: false }];
  }
  return segments;
}

/**
 * Cache for image src when using File (object URL). Used by marketing layout components.
 */
export interface ImageSrcCache {
  objectUrl: string | null;
  lastFile: File | null;
}

/**
 * Returns a display URL for an image (string or File). For File, creates and caches object URL.
 * Safe for SSR when input is string; for File, returns null if createObjectURL unavailable.
 */
export function getImageSrc(
  image: string | File | null | undefined,
  cache: ImageSrcCache
): string | null {
  if (image == null) return null;
  if (typeof image === 'string') {
    const t = image.trim();
    return t ? t : null;
  }
  if (image instanceof File) {
    if (cache.lastFile === image && cache.objectUrl) return cache.objectUrl;
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      if (cache.objectUrl) URL.revokeObjectURL(cache.objectUrl);
      cache.objectUrl = URL.createObjectURL(image);
      cache.lastFile = image;
      return cache.objectUrl;
    }
  }
  return null;
}

/**
 * Revokes cached object URL if present. Call in ngOnDestroy for components using getImageSrc with File.
 */
export function revokeImageSrcCache(cache: ImageSrcCache): void {
  if (cache.objectUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(cache.objectUrl);
  }
  cache.objectUrl = null;
  cache.lastFile = null;
}

/**
 * Sanitizes an image src string for safe use in [src] bindings. Returns null if src is empty.
 */
export function getSanitizedImageSrc(src: string | null, sanitizer: DomSanitizer): SafeUrl | null {
  if (!src || !src.trim()) return null;
  return sanitizer.bypassSecurityTrustUrl(src.trim());
}

enum PortfolioRoutePath {
  PODCAST = 'podcast-detail',
  MUSIC = 'music-detail',
  VIDEO = 'video-detail',
  CUSTOM = 'item-detail',
  MUSIC_ALBUM = 'music-album',
  PODCAST_ALBUM = 'podcast-series',
  VIDEO_SERIES = 'video-series',
  COLLECTION_DETAIL = 'collection-detail'
}

export function getPortfolioRoutePath(contentType?: string): PortfolioRoutePath | undefined {
  const contentTypeLower = contentType?.toLowerCase();

  switch (contentTypeLower) {
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST.toLowerCase():
      return PortfolioRoutePath.PODCAST;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC.toLowerCase():
      return PortfolioRoutePath.MUSIC;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO.toLowerCase():
      return PortfolioRoutePath.VIDEO;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM.toLowerCase():
      return PortfolioRoutePath.CUSTOM;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC_ALBUM.toLowerCase():
      return PortfolioRoutePath.MUSIC_ALBUM;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST_SERIES.toLowerCase():
      return PortfolioRoutePath.PODCAST_ALBUM;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO_SERIES.toLowerCase():
      return PortfolioRoutePath.VIDEO_SERIES;
    case PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.COLLECTION.toLowerCase():
      return PortfolioRoutePath.COLLECTION_DETAIL;
    default:
      return undefined;
  }
}

/**
 * Build fan preview URL for portfolio content (dashboard portfolio detail screen).
 * @param item - Portfolio item with sk, type (MUSIC/PODCAST/VIDEO), and isIndividual flag
 * @param userName - Creator's user_name (portfolio owner)
 * @returns Dashboard portfolio path, or empty string if invalid
 */
export function getPortfolioContentURL(
  item: { sk?: string; type?: string; isIndividual?: boolean },
  userName: string
): string {
  if (!item?.sk || !userName) return '';
  const encodedSk = (item.sk || '').replace(/#/g, '-');
  const typeUpper = (item.type || '').toUpperCase();
  const { PORTFOLIO_TYPE } = PORTFOLIO_TYPE_CONSTS;
  const isAlbum = item.isIndividual === false;
  const contentType = isAlbum
    ? (typeUpper === PORTFOLIO_TYPE.MUSIC
        ? PORTFOLIO_TYPE.MUSIC_ALBUM
        : typeUpper === PORTFOLIO_TYPE.PODCAST
          ? PORTFOLIO_TYPE.PODCAST_SERIES
          : typeUpper === PORTFOLIO_TYPE.VIDEO
            ? PORTFOLIO_TYPE.VIDEO_SERIES
            : typeUpper === PORTFOLIO_TYPE.CUSTOM
              ? PORTFOLIO_TYPE.COLLECTION
              : typeUpper)
    : typeUpper;
  const routePath = getPortfolioRoutePath(contentType);
  if (!routePath) return '';
  return `/dashboard/portfolio/${userName}/${routePath}/${encodedSk}`;
}

export interface PortfolioTypeTerms {
  collection: string;
  collections: string;
  item: string;
  items: string;
  action: string;
}

export function getPortfolioTypeTerms(portfolioType: string): PortfolioTypeTerms {
  const type = (portfolioType || '').toUpperCase();
  const { PORTFOLIO_TYPE } = PORTFOLIO_TYPE_CONSTS;
  
  switch (type) {
    case PORTFOLIO_TYPE.MUSIC:
      return { collection: 'album', collections: 'albums', item: 'song', items: 'songs', action: 'listen' };
    case PORTFOLIO_TYPE.VIDEO:
      return { collection: 'playlist', collections: 'playlists', item: 'video', items: 'videos', action: 'watch' };
    case PORTFOLIO_TYPE.PODCAST:
      return { collection: 'series', collections: 'series', item: 'episode', items: 'episodes', action: 'listen' };
    case PORTFOLIO_TYPE.CUSTOM:
    default:
      return { collection: 'collection', collections: 'collections', item: 'portfolio item', items: 'portfolio items', action: 'view' };
  }
}

export function parseReleaseDate(value: any): { type: 'year' | 'date' | null; value: string | number | null } {
  if (value === null || value === undefined || value === '') {
    return { type: null, value: null };
  }
  if (typeof value === 'string') {
    return { type: STEP_1_BASIC_INFO_CONSTS.RELEASE_TYPE_YEAR, value: value };
  }
  if (typeof value === 'number') {
    return { type: STEP_1_BASIC_INFO_CONSTS.RELEASE_TYPE_DATE, value: value };
  }
  
  return { type: null, value: null };
}

export function formatReleaseDateForDisplay(value: any): any {
  const parsed = parseReleaseDate(value);
  if (parsed.type === STEP_1_BASIC_INFO_CONSTS.RELEASE_TYPE_YEAR) {
    return parsed.value as string;
  }
  if (parsed.type === STEP_1_BASIC_INFO_CONSTS.RELEASE_TYPE_DATE) {
    return new Date(parsed.value as number);
  }
  return '';
}

export function formatDisplayDomain(domain: string): string {
  if (!domain) return '';
  return domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '');
}

export function decodeHtmlEntities(htmlContent: string): string {
  if (htmlContent == null || typeof htmlContent !== 'string') return '';
  return htmlContent
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&mdash;/g, '-')
    .replace(/&ndash;/g, '-')
    .replace(/&hellip;/g, '...')
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&lsquo;/g, "'")
    .replace(/&rsquo;/g, "'")
    .replace(/&copy;/g, '(c)')
    .replace(/&reg;/g, '(R)')
    .replace(/&trade;/g, '(TM)')
    .replace(/&[a-zA-Z]+;/g, '')
    .replace(/&#\d+;/g, '')
    .replace(/&#x[a-fA-F0-9]+;/g, '');
}

export function formatDurationFromHM(hours: number, minutes: number): string {
  if (!hours) {
    return `${minutes} min`;
  }

  const hourLabel = hours === Application.ONE ? 'hr' : 'hrs';
  return `${hours} ${hourLabel} ${minutes} min`;
}

/**
 * Returns true if the URL is external (full URL, protocol-relative, mailto, tel, or empty/#).
 * Internal routes (e.g. /dashboard, /profile/xyz) return false so they can use SPA navigation.
 */
export function isExternalLink(url: string | null | undefined): boolean {
  if (!url || url.trim() === '' || url === '#') {
    return true;
  }
  const trimmed = url.trim().toLowerCase();
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:')
  );
}

export function getSelectedBadgeGroups(form: any): string[] {
  const badgeGroups = form.get('badge_groups');
  if (!badgeGroups) return [];
  return badgeGroups.value
    .filter((item: any) => item.isSelected)
    .map((item: any) => item.sk);
}